import { useContext } from "react"
import { QuestionPanel } from "../../components/question/QuestionPanel"
import { QuestionnaireContext } from "../../context/QuestionnaireContext"
import { useNhsAppJs } from "../../hooks/useNhsAppJs"
import { AuthenticationContext } from "../../context/AuthenticationContext"
import { QuestionnaireComplete } from "./QuestionnaireComplete"

/**
 * Questionnaire Flow:
 *
 * `QuestionnaireStart.tsx` -> (`QuestionPage.tsx` -> `...` -> `QuestionPage.tsx`) -> `QuestionnaireSummary.tsx`
 *
 * This page:
 * - Uses `QuestionnaireContext` to access the current step, questions, and responses.
 * - Displays the current question and manages user responses.
 * - Provides navigation controls to move to the next or previous question.
 *
 * @returns {JSX.Element | null} The rendered page.
 */
export function QuestionPage(): JSX.Element | null {
  // Navigation
  const { navigateToPage } = useNhsAppJs()

  // Questionnaire Context
  const questionnaireContext = useContext(QuestionnaireContext)
  const { changeRequested } = useContext(AuthenticationContext)

  if (changeRequested) return <QuestionnaireComplete />

  if (!questionnaireContext) {
    throw new Error("QuestionPage must be used within a QuestionnaireProvider")
  }
  const { pages, step, responses, setResponses, questions } = questionnaireContext

  const question = step && step >= 1 ? questions[step - 1] : null

  function goToStep(stepNumber: number): void {
    if (stepNumber <= pages) {
      navigateToPage({ relativePath: `../${stepNumber}` })
    } else {
      navigateToPage({ relativePath: "../../summary" })
    }
  }

  if (!question || step === null) return null

  return (
    <QuestionPanel
      question={question}
      responses={responses}
      setResponses={setResponses}
      key={step}
      onContinue={() => {
        goToStep(step + 1)
      }}
      onPrevious={
        step > 1
          ? () => {
              goToStep(step - 1)
            }
          : undefined
      }
    />
  )
}
